import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft,
  Phone,
  Package,
  CheckCircle2,
  XCircle,
  X,
  AlertCircle,
  AlertTriangle,
  Wallet,
  MapPin,
  Truck,
  Home,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VENDOR_STATUS_FLOW = {
  pending: ['confirmed', 'cancelled'],
  confirmed: ['preparing', 'cancelled'],
  preparing: ['ready', 'cancelled'],
  ready: ['out_for_delivery', 'delivered', 'cancelled'],
  out_for_delivery: ['delivered'],
  delivered: [],
  cancelled: [],
};

const STATUS_LABELS = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  preparing: 'Preparing',
  ready: 'Ready',
  out_for_delivery: 'Out for delivery',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
};

const STATUS_BADGE_STYLES = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  confirmed: 'bg-blue-50 text-blue-700 border-blue-200',
  preparing: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  ready: 'bg-purple-50 text-purple-700 border-purple-200',
  out_for_delivery: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  delivered: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  cancelled: 'bg-red-50 text-red-700 border-red-200',
};

const formatDateTime = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
};

const StatusBadge = ({ status, size = 'md' }) => (
  <span
    className={`inline-flex items-center px-2.5 py-1 rounded-full font-medium border ${
      STATUS_BADGE_STYLES[status] || 'bg-gray-50 text-gray-600 border-gray-200'
    } ${size === 'sm' ? 'text-[11px] px-2 py-0.5' : 'text-xs'}`}
  >
    {STATUS_LABELS[status] || status}
  </span>
);

const VendorOrderDetail = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [updatingItemId, setUpdatingItemId] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [pendingDeliver, setPendingDeliver] = useState(null); // {itemId} when confirmation needed
  const [markingPaid, setMarkingPaid] = useState(false);

  const fetchOrder = async () => {
    setLoading(true);
    setNotFound(false);
    try {
      const res = await axios.get(`${API_URL}/api/vendor/orders/${orderId}`, {
        withCredentials: true,
      });
      setOrder(res.data);
    } catch (err) {
      if (err?.response?.status === 404) setNotFound(true);
      else console.error('Failed to fetch order:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (orderId) fetchOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  // Whether marking THIS item as delivered will derive the order status to "delivered"
  // (= it's the last non-delivered, non-cancelled item).
  const willCompleteOrder = (itemId) => {
    if (!order) return false;
    const items = order.items || [];
    const remaining = items.filter(
      (it) => it.item_id !== itemId && it.status !== 'delivered' && it.status !== 'cancelled'
    );
    return remaining.length === 0;
  };

  const updateItemStatus = async (itemId, newStatus) => {
    setUpdatingItemId(itemId);
    setActionError(null);
    try {
      const res = await axios.patch(
        `${API_URL}/api/vendor/orders/${orderId}/items/${itemId}/status`,
        { status: newStatus },
        { withCredentials: true }
      );
      setOrder(res.data);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        'Failed to update status';
      setActionError(detail);
    } finally {
      setUpdatingItemId(null);
    }
  };

  // Wraps updateItemStatus: if action is "delivered" + COD + not yet paid + this is the
  // final outstanding item, prompt for confirmation first.
  const requestUpdateItemStatus = (itemId, newStatus) => {
    if (
      newStatus === 'delivered' &&
      order?.payment_method === 'cod' &&
      order?.payment_status !== 'paid' &&
      willCompleteOrder(itemId)
    ) {
      setPendingDeliver({ itemId });
      return;
    }
    updateItemStatus(itemId, newStatus);
  };

  const confirmDeliver = () => {
    const id = pendingDeliver?.itemId;
    setPendingDeliver(null);
    if (id) updateItemStatus(id, 'delivered');
  };

  const markOrderPaid = async () => {
    setMarkingPaid(true);
    setActionError(null);
    try {
      const res = await axios.patch(
        `${API_URL}/api/vendor/orders/${orderId}/payment`,
        {},
        { withCredentials: true }
      );
      setOrder(res.data);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        'Failed to mark as paid';
      setActionError(detail);
    } finally {
      setMarkingPaid(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  if (notFound || !order) {
    return (
      <div data-testid="vendor-order-detail-page">
        <BackLink onClick={() => navigate('/vendor/orders')} />
        <div className="bg-white rounded-2xl border border-surface-border p-12 flex flex-col items-center justify-center text-text-muted">
          <AlertCircle size={48} className="mb-4 opacity-40" />
          <p className="font-body text-lg">Order not found</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="vendor-order-detail-page">
      <BackLink onClick={() => navigate('/vendor/orders')} />

      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3 mb-1">
        <div>
          <h1 className="font-heading text-3xl font-bold text-text-primary">
            {order.order_number}
          </h1>
          <p className="font-body text-sm text-text-muted mt-1">
            Placed {formatDateTime(order.created_at)}
            {order.updated_at && order.updated_at !== order.created_at
              ? ` · Updated ${formatDateTime(order.updated_at)}`
              : ''}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <StatusBadge status={order.status} />
          <p className="font-body text-xs text-text-muted">Order status (auto)</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-5 mt-6">
        {/* Left column — items + per-item status */}
        <div className="lg:col-span-2 space-y-3">
          <SectionHeading>Items</SectionHeading>

          {actionError && (
            <div className="rounded-xl bg-red-50 border border-red-200 text-red-700 px-3 py-2 text-sm font-body flex items-start gap-2">
              <XCircle size={16} className="mt-0.5 shrink-0" />
              <span>{actionError}</span>
            </div>
          )}

          {(order.items || []).map((it) => (
            <ItemCard
              key={it.item_id}
              item={it}
              orderStatus={order.status}
              busy={updatingItemId === it.item_id}
              onUpdate={(next) => requestUpdateItemStatus(it.item_id, next)}
            />
          ))}
        </div>

        {/* Right column — customer / payment / totals */}
        <div className="space-y-5">
          {/* Customer */}
          <div className="bg-white rounded-2xl border border-surface-border p-4">
            <SectionHeading>Customer</SectionHeading>
            <p className="font-body font-semibold text-text-primary mt-2">
              {order.customer_name || 'Customer'}
            </p>
            {order.customer_phone && (
              <a
                href={`tel:${order.customer_phone}`}
                className="font-body text-sm text-brand-primary inline-flex items-center gap-1.5 mt-1 hover:underline"
                data-testid="order-customer-phone"
              >
                <Phone size={14} />
                {order.customer_phone}
              </a>
            )}
            {(order.customer_apartment_name || order.customer_room_number) && (
              <div className="mt-3 pt-3 border-t border-surface-border">
                <p className="font-body text-[11px] uppercase tracking-wide text-text-muted mb-1">
                  Apartment / Room
                </p>
                <p className="font-body text-sm text-text-primary inline-flex items-center gap-1.5">
                  <MapPin size={13} className="text-text-muted" />
                  {order.customer_apartment_name || '—'}
                  {order.customer_room_number ? ` · ${order.customer_room_number}` : ''}
                </p>
              </div>
            )}
          </div>

          {/* Notes from customer (if any) */}
          {order.notes && (
            <div className="bg-white rounded-2xl border border-surface-border p-4">
              <SectionHeading>Notes from customer</SectionHeading>
              <p className="font-body text-sm text-text-primary mt-2">{order.notes}</p>
            </div>
          )}

          {/* Payment + total */}
          <div className="bg-white rounded-2xl border border-surface-border p-4">
            <SectionHeading>Payment</SectionHeading>
            <div className="space-y-1.5 mt-2">
              <Row
                label="Method"
                value={(order.payment_method || 'cod').toUpperCase()}
              />
              <Row label="Status" value={order.payment_status || 'pending'} />
              <Row
                label="Subtotal"
                value={`₹${Number(order.subtotal || 0).toFixed(2)}`}
              />
              <div className="flex items-center justify-between pt-2 mt-1 border-t border-surface-border">
                <span className="font-body text-sm font-medium text-text-primary">
                  Total
                </span>
                <span className="font-heading font-bold text-lg text-text-primary">
                  ₹{Number(order.total_amount || 0).toFixed(2)}
                </span>
              </div>
            </div>

            {order.payment_status !== 'paid' && (
              <Button
                onClick={markOrderPaid}
                disabled={markingPaid}
                className="mt-3 w-full rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
                data-testid="order-mark-paid-btn"
              >
                <Wallet size={16} />
                {markingPaid ? 'Marking…' : 'Mark as paid'}
              </Button>
            )}
            {order.payment_status === 'paid' && (
              <div className="mt-3 inline-flex items-center gap-2 text-emerald-700 font-body text-sm">
                <CheckCircle2 size={16} />
                Payment received
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Confirm "Mark as delivered" — also flips payment to paid for COD */}
      <Dialog
        open={!!pendingDeliver}
        onOpenChange={(open) => !open && setPendingDeliver(null)}
      >
        <DialogContent className="sm:max-w-md" data-testid="deliver-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <AlertTriangle size={18} className="text-amber-500" />
              Confirm delivery
            </DialogTitle>
          </DialogHeader>
          <div className="font-body text-sm text-text-secondary space-y-2 py-2">
            <p>
              Marking this as the final delivered item will complete the order.
            </p>
            <p className="rounded-xl bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2">
              This will <strong>also mark the order as paid</strong> (COD cash collected).
              If you haven't received the cash yet, cancel this and confirm only after
              you do.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              className="rounded-xl"
              onClick={() => setPendingDeliver(null)}
              data-testid="deliver-confirm-cancel"
            >
              Cancel
            </Button>
            <Button
              className="rounded-xl bg-brand-primary hover:bg-brand-primary-hover text-white gap-2"
              onClick={confirmDeliver}
              data-testid="deliver-confirm-ok"
            >
              <CheckCircle2 size={16} />
              Yes, deliver &amp; mark paid
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ItemCard = ({ item, busy, onUpdate }) => {
  const allowedNext = VENDOR_STATUS_FLOW[item.status] || [];
  const isTerminal = allowedNext.length === 0;

  return (
    <div
      className={`bg-white rounded-2xl border border-surface-border p-4 ${
        item.status === 'cancelled' ? 'opacity-70' : ''
      }`}
      data-testid={`order-item-${item.item_id}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-14 h-14 rounded-xl bg-background-secondary overflow-hidden shrink-0">
          {item.image_url ? (
            <img
              src={item.image_url}
              alt={item.name}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-text-muted">
              <Package size={20} />
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-body font-semibold text-text-primary">
              {item.name}
            </p>
            <StatusBadge status={item.status} size="sm" />
          </div>
          <p className="font-body text-sm text-text-muted mt-0.5">
            ₹{Number(item.price || 0).toFixed(0)} × {item.quantity}
          </p>
        </div>

        <p className="font-heading font-bold text-base text-text-primary shrink-0">
          ₹{Number(item.subtotal || 0).toFixed(0)}
        </p>
      </div>

      {/* Delivery info */}
      <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-body bg-background-secondary text-text-secondary px-2.5 py-1 rounded-full">
        {item.delivery_type === 'home_delivery' ? (
          <>
            <Home size={12} />
            Home delivery
          </>
        ) : (
          <>
            <Truck size={12} />
            Pickup at <strong className="font-medium text-text-primary">{item.pickup_point_name || '—'}</strong>
          </>
        )}
      </div>

      {/* Per-item status actions */}
      <div className="mt-3 pt-3 border-t border-surface-border">
        {isTerminal ? (
          <p className="font-body text-xs text-text-muted italic">
            This item is {STATUS_LABELS[item.status]?.toLowerCase()} — no further actions.
          </p>
        ) : (
          <div>
            <p className="font-body text-[11px] uppercase tracking-wide text-text-muted mb-2">
              Update item status
            </p>
            <div className="flex flex-wrap gap-2">
              {allowedNext.map((next) => {
                const isCancel = next === 'cancelled';
                return (
                  <Button
                    key={next}
                    onClick={() => onUpdate(next)}
                    disabled={busy}
                    size="sm"
                    variant={isCancel ? 'outline' : 'default'}
                    className={`rounded-xl gap-1.5 h-8 text-xs ${
                      isCancel
                        ? 'border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700'
                        : 'bg-brand-primary hover:bg-brand-primary-hover text-white'
                    }`}
                    data-testid={`item-status-action-${item.item_id}-${next}`}
                  >
                    {isCancel ? <X size={14} /> : <CheckCircle2 size={14} />}
                    Mark as {STATUS_LABELS[next]}
                  </Button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const SectionHeading = ({ children }) => (
  <p className="font-body text-xs uppercase tracking-wide text-text-muted">
    {children}
  </p>
);

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between">
    <span className="font-body text-sm text-text-muted">{label}</span>
    <span className="font-body text-sm text-text-primary capitalize">{value}</span>
  </div>
);

const BackLink = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex items-center gap-1.5 text-text-secondary hover:text-text-primary font-body text-sm mb-4"
    data-testid="back-to-orders-link"
  >
    <ArrowLeft size={16} />
    Back to orders
  </button>
);

export default VendorOrderDetail;
